import React, {useEffect, useState, useCallback} from 'react';
import {Col, Row, Breadcrumb, Select, Space} from "antd";
import {FileProtectOutlined, HomeOutlined} from '@ant-design/icons';
import TotalDistance from "./Charts/TotalDistance";
import OnlineDonut from "./Charts/OnlineDonut";
import CruiseStatus from "./Charts/cruisStatus";
import GroupCars from "./Charts/groupCars";
import StatusCards from "./Charts/StatusCards";
import AverageFuelUsed from "./Charts/AverageFuelUsed";
import {useSelector} from "react-redux";
import {io} from "socket.io-client";

const {Option} = Select;

const Index = () => {

    const {user} = useSelector((state) => state.auth);
    const [loading, setLoading] = useState(false);
    const [onlineChartData, setOnlineChartData] = useState([]);
    const [faultData, setFaultData] = useState([]);
    const [cruiseData, setCruiseData] = useState([]);
    const [statusData, setStatusData] = useState({
        red: 0,
        orange: 0,
        green: 0,
        gray: 0,
    });

    const [selectedBrand, setSelectedBrand] = useState(null);
    const [selectedFuel, setSelectedFuel] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [groupTitles, setGroupTitles] = useState([]);

    const filterData = useCallback((data) => {
        return data.filter(vehicle =>
            (!selectedBrand || vehicle.brand === selectedBrand) &&
            (!selectedFuel || vehicle.fuelType === selectedFuel) &&
            (!selectedGroup || vehicle.groupTitle === selectedGroup)
        );
    }, [selectedBrand, selectedFuel, selectedGroup]);

    useEffect(() => {
        setLoading(true);
        if (!user?.token) return;
        const socket = io('https://live.myoctoopidev.com', {
            reconnection: true,
            transports: ['websocket'],
            reconnectionDelay: 2000,
            reconnectionAttempts: 5,
            withCredentials: true,
            query: {'x-access-token': user?.token},
            transportOptions: {binary: true},
        });

        socket.on('map', (incomingData) => {
            const filteredData = filterData(incomingData);

            const uniqueGroupTitles = [...new Set(
                incomingData
                    .map(vehicle => vehicle.groupTitle)
                    .filter(title => title && title.trim() !== '')
            )];

            setGroupTitles(uniqueGroupTitles);
            const statusCounts = filteredData.reduce((acc, vehicle) => {
                const status = vehicle.status || "gray";
                acc[status] = (acc[status] || 0) + 1;
                return acc;
            }, {red: 0, orange: 0, green: 0, gray: 0});

            const formattedData = filteredData.reduce((acc, vehicle) => {
                const statusKey = vehicle.onlineStatus === 'online' ? 'online' : 'offline';
                acc[statusKey].count += 1;
                acc[statusKey].vehicles.push({
                    plateNumber: vehicle.plateNumber,
                    operationNumber: vehicle.operationNumber,
                });
                return acc;
            }, {
                online: {count: 0, vehicles: []},
                offline: {count: 0, vehicles: []},
            });

            const onlineFormattedData = [
                {type: 'Online', value: formattedData.online.count, vehicles: formattedData.online.vehicles},
                {type: 'Offline', value: formattedData.offline.count, vehicles: formattedData.offline.vehicles},
            ];

            const cruiseData = filteredData.reduce((acc, vehicle) => {
                const status = vehicle.cruiseStatus || 'Bilinmeyen';
                if (!acc[status]) acc[status] = [];
                acc[status].push({
                    plateNumber: vehicle.plateNumber,
                    operationNumber: vehicle.operationNumber,
                });
                return acc;
            }, {});

            const cruiseFormattedData = Object.entries(cruiseData).map(([status, vehicles]) => ({
                type: status,
                value: vehicles.length,
                vehicles,
            }));

            const faultData = filteredData.reduce((acc, vehicle) => {
                const status = vehicle.status || 'Bilinmeyen';
                const type = {
                    red: 'Arızalı Araçlar',
                    orange: 'Uyarılı Araçlar',
                    green: 'Hatasız Aktif Araçlar',
                    gray: 'Hatasız İnaktif Araçlar',
                }[status] || 'Bilinmeyen';

                if (!acc[type]) acc[type] = [];
                acc[type].push({
                    plateNumber: vehicle.plateNumber,
                    operationNumber: vehicle.operationNumber,
                });
                return acc;
            }, {});

            const faultFormattedData = Object.entries(faultData).map(([type, vehicles]) => ({
                type,
                value: vehicles.length,
                vehicles,
            }));

            setStatusData(statusCounts);
            setCruiseData(cruiseFormattedData);
            setFaultData(faultFormattedData);
            setOnlineChartData(onlineFormattedData);
            setLoading(false);
        });

        socket.on('reconnect', () => console.log('Yeniden bağlandınız'));
        socket.on('connect_error', (error) => setLoading(false));

        return () => socket.disconnect();
    }, [user?.token, filterData]);


    console.log(onlineChartData)


    return (
        <>
            <Row style={{marginBottom: 20}} align="middle" gutter={16}>
                <Col>
                    <Breadcrumb
                        items={[
                            {href: '/', title: <HomeOutlined/>},
                            {
                                href: '/reports/manager-report',
                                title: (
                                    <>
                                        <FileProtectOutlined/>
                                        <span>Yönetici Ekranı</span>
                                    </>
                                ),
                            },
                        ]}
                    />
                </Col>
                <Col flex="auto">
                    <Space>
                        <Select
                            size="small"
                            placeholder="Araç Markası Seç"
                            style={{width: 150}}
                            onChange={value => setSelectedBrand(value)}
                            allowClear
                        >
                            <Option value="mercedes">Mercedes</Option>
                            <Option value="otokar">Otokar</Option>
                        </Select>
                        <Select
                            size="small"
                            placeholder="Benzin Türü Seç"
                            style={{width: 150}}
                            onChange={value => setSelectedFuel(value)}
                            allowClear
                        >
                            <Option value="cng">Gazlı</Option>
                            <Option value="diesel">Dizel</Option>
                        </Select>
                        <Select
                            size="small"
                            placeholder="Grup Seç"
                            style={{width: 200}}
                            onChange={value => setSelectedGroup(value)}
                            allowClear
                        >
                            {groupTitles.map(group => (
                                <Option key={group} value={group}>{group}</Option>
                            ))}
                        </Select>
                    </Space>
                </Col>
            </Row>
            <StatusCards loading={loading} data={statusData}/>
            <Row style={{marginBottom: 10}} gutter={16}>
                <Col xs={24} xl={8}>
                    <OnlineDonut user={user} loading={loading} data={onlineChartData}/>
                </Col>
                <Col xs={24} xl={8}>
                    <CruiseStatus user={user} loading={loading} data={cruiseData}/>
                </Col>
                <Col xs={24} xl={8}>
                    <GroupCars user={user} loading={loading} data={faultData}/>
                </Col>
            </Row>
            <Row style={{marginBottom: 10}} gutter={16}>
                <Col xs={24} xl={12}>
                    <TotalDistance/>
                </Col>
                <Col xs={24} xl={12}>
                    <AverageFuelUsed/>
                </Col>
            </Row>
        </>
    );
};

export default Index;
